/**
 * Complete event schema based on craig.ddd.v3.0 streaming protocol
 * This matches the exact structure from example-stream.txt
 */

// ============================================================================
// Core Span Types - Hierarchical Structure
// ============================================================================

export interface SpanInfo {
    span_id: string;
    span_name: string;
    span_kind: 'cognitive_node' | 'cognitive_tool' | 'cognitive_workflow';
    parent_span_id: string | null; // null for root nodes
    response_id: string;
    depth: number; // 0 for root, increases with nesting
}

// ============================================================================
// Node and Tool Information
// ============================================================================

export interface NodeInfo {
    node_name: string;
    execution_context: string;
}

export interface ToolInfo {
    tool_name: string;
    bounded_context: string;
}

// ============================================================================
// Cognitive Processing Information
// ============================================================================

export interface CognitiveInfo {
    chain_of_thought: boolean;
    reasoning_step: boolean;
    cognitive_phase: CognitivePhase;

    // Optional fields based on phase
    node_name?: string;
    tool_name?: string;
    processing_type?: 'cognitive_node' | 'cognitive_tool';
    execution_time_ms?: number;

    // Request processing fields
    query_length?: number;
    request_preview?: string;
    original_query?: string;
    sanitized_query?: string;

    // Classification fields
    classification?: string;
    selected_route?: string;
    part_numbers?: string;
    reasoning_type?: string;

    // Tool execution fields
    step?: string;
    request_length?: number;
}

export type CognitivePhase =
    | 'node_start'
    | 'node_complete'
    | 'node_processing'
    | 'tool_start'
    | 'tool_update'
    | 'tool_complete'
    | 'request_acknowledgment'
    | 'signal_processing_start'
    | 'signal_complete'
    | 'classification_decision';

// ============================================================================
// Domain-Driven Design Information
// ============================================================================

export interface DomainInfo {
    subdomain: 'signal' | 'insight' | 'executive';
    bounded_context: string;
    entity: string;
    entity_id?: string;
}

// ============================================================================
// Complete Event Structure
// ============================================================================

export interface StreamingEvent {
    type: 'response.output_text.delta' | 'response.output_item.done';
    custom_outputs?: CustomOutputs | null;
    delta?: string;
    item_id: string;
    chunk_id: number;
    item?: {
        content: Array<{ text: string; type: string }>;
        id: string;
        type: string;
    };
}

export interface CustomOutputs {
    event_type: string; // e.g., "craig.cognitive.node.start"
    event_category?: 'cognitive';
    schema_version?: 'craig.ddd.v3.0';

    // Hierarchical span information
    span?: SpanInfo;

    // Node or tool information
    node?: NodeInfo;
    tool?: ToolInfo;

    // Cognitive processing information
    cognitive?: CognitiveInfo;

    // Domain information
    domain?: DomainInfo;
}

// ============================================================================
// Event Type Constants
// ============================================================================

export const CognitiveEventTypes = {
    // Node lifecycle
    NODE_START: 'craig.cognitive.node.start',
    NODE_COMPLETE: 'craig.cognitive.node.complete',
    NODE_PROCESSING: 'craig.cognitive.processing',

    // Tool lifecycle
    TOOL_START: 'craig.cognitive.tool.start',
    TOOL_UPDATE: 'craig.cognitive.tool.update',
    TOOL_COMPLETE: 'craig.cognitive.tool.complete',

    // Request processing
    REQUEST_RECEIVED: 'craig.cognitive.request.received',
    CLASSIFICATION_DONE: 'craig.cognitive.classification.done',

    // LLM tokens
    LLM_TOKEN: 'response.llm.token'
} as const;

// ============================================================================
// Span Management Types
// ============================================================================

export interface SpanNode {
    span: SpanInfo;
    node?: NodeInfo;
    tool?: ToolInfo;
    cognitive?: CognitiveInfo;
    domain?: DomainInfo;

    events: ProcessedEvent[];
    children: SpanNode[];

    // UI state
    isExpanded: boolean;
    isActive: boolean;
    startTime?: number;
    endTime?: number;
    duration?: number;
}

export interface ProcessedEvent {
    chunk_id: number;
    event_type: string;
    delta: string;
    timestamp: number;
    cognitive_phase?: CognitivePhase;
    metadata?: any;
}

// ============================================================================
// Conversation State with Cognitive Information
// ============================================================================

export interface CognitiveStreamState {
    // Span hierarchy
    rootSpans: SpanNode[];
    spanRegistry: Map<string, SpanNode>;

    // UI state persistence
    uiState?: {
        isChainOfThoughtCollapsed: boolean;
        // Add other UI states here as needed (e.g., domain filters, individual span states)
    };

    // Active streaming
    activeSpanId?: string;
    currentPhase?: CognitivePhase;

    // Response content
    responseContent: string;
    responseTokens: string[];

    // Metrics
    totalEvents: number;
    cognitiveEvents: number;
    llmTokens: number;
    nodesExecuted: number;
    toolsExecuted: number;
    totalDuration?: number;

    // Stream state
    isStreaming: boolean;
    streamStartTime?: number;
    streamEndTime?: number;
    lastEventTime?: number;
}

// ============================================================================
// Helper Functions for Event Classification
// ============================================================================

export function isCognitiveEvent(event: StreamingEvent): boolean {
    if (!event.custom_outputs?.event_type) return false;
    return event.custom_outputs.event_type.startsWith('craig.cognitive.');
}

export function isLLMToken(event: StreamingEvent): boolean {
    return (
        event.custom_outputs?.event_type === 'response.llm.token' ||
        (!event.custom_outputs?.event_type && event.type === 'response.output_text.delta')
    );
}

export function getSpanDepth(span: SpanInfo): number {
    return span.depth || 0;
}

export function isRootSpan(span: SpanInfo): boolean {
    return span.parent_span_id === null;
}

// ============================================================================
// Event Processing Pipeline Types
// ============================================================================

export interface EventProcessor {
    canProcess(event: StreamingEvent): boolean;
    process(event: StreamingEvent, state: CognitiveStreamState): void;
}

export interface EventPipeline {
    processors: EventProcessor[];
    process(event: StreamingEvent, state: CognitiveStreamState): void;
}
